import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroText, CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface RemoveTeaserDialogProps {
	open: boolean;
	dataRoomGuid: string | undefined;
	selectedTeasers: string[];
	teasers: DataRoomTeaser[] | undefined;
	setTeasersSelected: (selected: string[]) => void;
	close: () => void;
}

export const BulkDeleteTeasersDialog: React.FC<RemoveTeaserDialogProps> = ({
	open,
	dataRoomGuid,
	selectedTeasers,
	teasers,
	setTeasersSelected,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const sentTeasers = [] as DataRoomTeaser[];
	if (open) {
		selectedTeasers.map(selected => {
			const items = teasers?.filter(f => f.guid == selected && f.sentOn);
			if (items && items.length > 0) {
				sentTeasers.push(items[0]);
			}
		});
	}

	const onRemoveConfirmation = () => {
		deleteTeasers.mutate(selectedTeasers);
	};

	const deleteTeasers = useMutation<string[], unknown, string[]>(
		async (values: string[]) => {
			const updatedTeasers = await getResponseData<string[]>(
				api.post<string[]>(CerebroApiEndpoints.Teasers.DeleteMany(dataRoomGuid), values)
			);

			return updatedTeasers;
		},
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser Deleted.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));

				snackbar.enqueueSnackbar('There was a problem deleting the teaser.', { variant: 'error' });
			},
			onSettled: () => {
				setTeasersSelected([] as string[]);
			},
		}
	);

	if (open) {
		if (sentTeasers.length > 0) {
			return (
				<Dialog open={open} onClose={close}>
					<DialogTitle>Sent Teasers Can Not Be Deleted</DialogTitle>
					<DialogContent>
						<DialogContentText sx={{ color: '#000000' }}>
							<CerebroText sx={{ marginBottom: 2 }}>
								The following teasers cannot be deleted because they have already been sent:
							</CerebroText>

							{sentTeasers.map(teaser => (
								<CerebroText key={teaser.guid}>
									{teaser.firstName} {teaser.lastName} at {teaser.bankName}
								</CerebroText>
							))}

							<CerebroText sx={{ marginTop: 2 }}>
								No teasers have been deleted, Unselect the checkboxes for the sent teasers and then
								select the delete action.
							</CerebroText>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<CerebroTextButton onClick={close}>Close</CerebroTextButton>
					</DialogActions>
				</Dialog>
			);
		} else {
			return (
				<Dialog open={open} onClose={close}>
					<DialogTitle>Confirm Delete</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to delete the {selectedTeasers.length} selected teasers?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<CerebroTextButton onClick={close}>No</CerebroTextButton>
						<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
					</DialogActions>
				</Dialog>
			);
		}
	} else {
		return <></>;
	}
};
